import React from 'react';
import {
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface NotificationSettings {
  new_tasks: boolean;
  new_offers: boolean;
  messages: boolean;
  status_updates: boolean;
}

type SettingKey = keyof NotificationSettings;

const DEFAULT_SETTINGS: NotificationSettings = {
  new_tasks: true,
  new_offers: true,
  messages: true,
  status_updates: true,
};

interface Props {
  /** Кои превключватели да се покажат според ролята. */
  variant: 'professional' | 'client';
}

const NotificationSettingsPanel: React.FC<Props> = ({ variant }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery<NotificationSettings>({
    queryKey: ['myNotificationSettings'],
    queryFn: async () => {
      const response = await api.get('/api/v1/me');
      return { ...DEFAULT_SETTINGS, ...(response.data.notification_settings || {}) };
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const mutation = useMutation({
    mutationFn: async (update: Partial<NotificationSettings>) => {
      const response = await api.patch('/api/v1/me', {
        notification_settings: update,
      });
      return { ...DEFAULT_SETTINGS, ...(response.data.notification_settings || {}) };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['myNotificationSettings'], data);
    },
  });

  const handleChange = (field: SettingKey, checked: boolean) => {
    const previous = settings;
    queryClient.setQueryData(['myNotificationSettings'], {
      ...DEFAULT_SETTINGS,
      ...previous,
      [field]: checked,
    });
    mutation.mutate(
      { [field]: checked },
      {
        onError: () => {
          queryClient.setQueryData(['myNotificationSettings'], previous);
        },
      }
    );
  };

  const rows: Array<{ field: SettingKey; label: string; description: string }> =
    variant === 'professional'
      ? [
          {
            field: 'new_tasks',
            label: t('profile.new_tasks'),
            description: t('profile.new_tasks_description'),
          },
          {
            field: 'messages',
            label: t('profile.messages'),
            description: t('profile.messages_description'),
          },
          {
            field: 'status_updates',
            label: t('profile.status_updates'),
            description: t('profile.status_updates_description'),
          },
        ]
      : [
          {
            field: 'new_offers',
            label: t('profile.new_offers'),
            description: t('profile.new_offers_description'),
          },
          {
            field: 'messages',
            label: t('profile.messages'),
            description: t('profile.messages_description'),
          },
          {
            field: 'status_updates',
            label: t('profile.status_updates'),
            description: t('profile.status_updates_description'),
          },
        ];

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }} data-testid="notification-settings-section">
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <NotificationsIcon sx={{ mr: 1 }} />
        {t('profile.notification_settings')}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('profile.notification_settings_error')}
        </Alert>
      )}

      <List sx={{ width: '100%' }}>
        {rows.map((row) => (
          <ListItem key={row.field} disableGutters>
            <ListItemText primary={row.label} secondary={row.description} />
            <Switch
              checked={settings?.[row.field] ?? true}
              onChange={(event) => handleChange(row.field, event.target.checked)}
              disabled={isLoading || mutation.isPending}
              color="primary"
              inputProps={{ 'aria-label': row.label } as any}
              data-testid={`notification-setting-${row.field.replace(/_/g, '-')}`}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default NotificationSettingsPanel;
